/*
  "Buttery" smooth scroll for POINTER DEVICES (Lenis — assets/lenis.min.js).
  Wheel and keyboard only: touch devices bail out at the top of the IIFE and
  scroll natively — see the long note there for why that is the smoother
  choice rather than a gap.

  Wired to stay compatible with the theme's existing scroll-dependent
  features instead of fighting them:

  - Sticky header (page-header.js) show/hide + its IntersectionObserver
    enable/disable, and parallax.js's getBoundingClientRect loop, both rely
    on real window.scrollY / real layout scroll position. Lenis's default
    config (used below) drives the actual documentElement.scrollTop itself
    on every frame rather than faking scroll with a transform-wrapper, so
    those files needed zero changes — real scroll events keep firing, just
    eased.

  - Every drawer/modal in this theme locks the background by adding a body
    class that resolves to `overflow: hidden` (base.js, cart-drawer.js,
    search-drawer.js, localization-drawer.js, details-modal.js,
    pickup-availability.js — all under different class names). Rather than
    hardcode each one, this watches document.body's actual computed
    overflow and stops/starts Lenis off of that shared signal, so any
    current or future overlay that follows the same convention is covered
    automatically.

  - Each drawer/modal's own scrollable content gets `data-lenis-prevent` so
    it keeps scrolling natively (cart items, country list, mobile nav,
    product modal, etc.) even while Lenis is stopped for the page behind it.
*/
(function () {
  /*
    Touch devices get native scrolling, deliberately — this is the one place
    where NOT running Lenis is the smoother choice.

    Lenis only ever smoothed the wheel here: `syncTouch` defaults to false and
    was never set, so a phone got none of the easing below. What it did get
    was the cost of it. Lenis's internal VirtualScroll attaches touchstart /
    touchmove / touchend with { passive: false } unconditionally, without
    checking whether syncTouch is on, and a non-passive touchmove listener on
    the document means the browser must wait for JS before it can move the
    page — it loses the threaded-scroll fast path it would otherwise use. So
    mobile paid the tax and got nothing back.

    Turning syncTouch ON was the other option and is the wrong one. It hands
    finger-tracking to a rAF loop, which reads as floaty and detached from the
    finger, and it suppresses the native scroll signals iOS Safari uses to
    collapse its address bar. Native momentum scrolling on iOS and Android is
    already excellent and is what visitors' muscle memory expects.

    `(pointer: coarse)` — PRIMARY input is a finger. A touchscreen laptop with
    a trackpad reports `(pointer: fine)` and so keeps wheel smoothing, which
    is correct: it has a wheel to smooth. Checked once at load, like the
    reduced-motion bail above it; primary pointer type does not meaningfully
    change mid-session.

    The class is what keeps custom.css honest. Its native scroll-snap fallback
    is written `html:not(.lenis):not(.smooth-scroll-off)` because that
    fallback exists for ONE case — Lenis failing to load — and must not fire
    for this one, where the absence is deliberate. Without the class an iPad
    in landscape (>= 990px, coarse pointer) would fall into CSS `y proximity`
    snapping, which is the grabby feel this change removes. Set before the
    `typeof Lenis` guard below so it lands even if the library never arrived.
  */
  if (window.matchMedia("(pointer: coarse)").matches) {
    document.documentElement.classList.add("smooth-scroll-off");
    return;
  }

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (typeof Lenis === "undefined") return;

  /*
    `lerp`, not `duration` + `easing` — these are alternative modes in Lenis,
    and which one is chosen is most of what "smooth" feels like here.

    In duration mode every wheel event calls scrollTo() afresh, which
    restarts a full 1s easeOutExpo curve from wherever the page currently
    is. easeOutExpo covers roughly half its distance in its first tenth of
    time, so continuous wheeling becomes a rapid-fire sequence of restarted
    lurch-then-crawl curves: it reads as sticky and laggy rather than
    smooth, and it is worst during exactly the sustained scrolling a long
    homepage is made of.

    lerp mode instead eases the current position toward a moving target by a
    fixed fraction each frame, frame-rate independent. Repeated wheel input
    accumulates into that one target rather than restarting anything, so a
    long scroll is a single continuous glide. This is the mode that produces
    the feel Lenis is known for.

    0.085 is slightly below the 0.1 default — a longer, heavier glide, which
    suits the brand's slower luxury pacing. Lower is smoother but starts to
    feel disconnected from the wheel; much above 0.12 and the smoothing
    stops being perceptible at all.
  */
  var lenis = new Lenis({
    lerp: 0.085,
    smoothWheel: true,
  });
  window.lenis = lenis;

  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  document
    .querySelectorAll(
      [
        "cart-drawer",
        "search-drawer",
        "localization-drawer",
        "mega-menu-section",
        '[class*="drawer"]',
        '[class*="modal"]',
        '[class*="popup"]',
        '[id*="drawer"]',
      ].join(", "),
    )
    .forEach(function (el) {
      el.setAttribute("data-lenis-prevent", "");
    });

  var wasLocked = false;
  function syncWithBodyLock() {
    var isLocked = getComputedStyle(document.body).overflow === "hidden";
    if (isLocked === wasLocked) return;
    wasLocked = isLocked;
    if (isLocked) {
      lenis.stop();
    } else {
      lenis.start();
    }
  }
  new MutationObserver(syncWithBodyLock).observe(document.body, {
    attributes: true,
    attributeFilter: ["class", "style"],
  });
  syncWithBodyLock();

  // Lenis only hijacks wheel/touch by default (keyboard scroll is native
  // browser instant-jump). Route the common scroll keys through
  // lenis.scrollTo() too so keyboard scrolling gets the same eased feel —
  // skipped for form controls/links/buttons (native key behavior there)
  // and anything inside an open drawer/modal (data-lenis-prevent, same as
  // wheel/touch).
  var KEYBOARD_SKIP_TAGS = ["INPUT", "TEXTAREA", "SELECT", "BUTTON", "A"];
  document.addEventListener("keydown", function (e) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (lenis.isStopped) return;

    var target = e.target;
    if (
      KEYBOARD_SKIP_TAGS.indexOf(target.tagName) !== -1 ||
      target.isContentEditable ||
      (target.closest && target.closest("[data-lenis-prevent]"))
    ) {
      return;
    }

    var viewport = window.innerHeight * 0.9;
    var delta;
    switch (e.key) {
      case "ArrowDown":
        delta = 100;
        break;
      case "ArrowUp":
        delta = -100;
        break;
      case "PageDown":
        delta = viewport;
        break;
      case "PageUp":
        delta = -viewport;
        break;
      case " ":
        delta = e.shiftKey ? -viewport : viewport;
        break;
      case "Home":
        e.preventDefault();
        lenis.scrollTo("top", { duration: 0.5 });
        return;
      case "End":
        e.preventDefault();
        lenis.scrollTo("bottom", { duration: 0.5 });
        return;
      default:
        return;
    }
    e.preventDefault();
    lenis.scrollTo(lenis.scroll + delta, { duration: 0.5 });
  });
})();
